import { useCallback, useState, useMemo, memo, type ChangeEvent } from 'react';

import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { IconButton, Menu, MenuItem, TableCell, TableRow, Checkbox, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import ClassesContainer from './ClassesContainer';
import ClassesDateRange from './ClassesDateRange';
import ArchiveChip from './ClassPage/ArchiveChip';
import DraftChip from './ClassPage/DraftChip';
import Instructors from './Instructors';
import { type ClassWithInstructors, type ColumnVisibility } from './types';
import { ClassStatus } from '../../../../context/types';
import { getUrls } from '../../../../utils/url';

// Screen reader only styles
const GlobalStyle = `
    .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
    }
`;

const ClassLink = styled(Link)`
    color: ${(props) => props.theme.colors.primary.main};
`;

const MenuLink = styled(Link)`
    color: inherit;
`;

const StyledIconButton = styled(IconButton)`
    svg {
        width: 32px;
        height: 32px;
    }
`;

const SmallIconButton = styled(IconButton)`
    padding: 4px;
    svg {
        width: 20px;
        height: 20px;
    }
`;

const Cell = styled(TableCell)<{ width?: string }>`
    padding: 0 16px;

    /* Apply width if provided */
    ${(props) =>
        props.width &&
        `
        width: ${props.width};
        min-width: ${props.width};
        max-width: ${props.width};
    `}
`;

const CheckboxCell = styled(TableCell)`
    padding: 0 16px;
    width: 48px;
    min-width: 48px;
    max-width: 48px;
    text-align: center;
    overflow: visible; /* Allow tooltips to show */
`;

const ClassNameCell = styled(TableCell)`
    padding: 0 16px;
    width: 35%;
    min-width: 200px;

    /* Use flexbox to ensure chips are always visible */
    .class-name-content {
        display: flex;
        align-items: center;
        gap: 8px;
        min-width: 0; /* Allow flex item to shrink */
        overflow: hidden;
        width: 100%;
    }

    .class-name-text {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        flex: 1;
        min-width: 0;
    }

    .class-status-chips {
        display: flex;
        gap: 4px;
        flex-shrink: 0; /* Prevent chips from being compressed */
    }
`;

const InstructorCell = styled(TableCell)`
    padding: 0 16px;
    width: 25%;
    min-width: 150px;
    overflow: hidden;

    /* Handle instructor content overflow */
    > div {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

const DateCell = styled(TableCell)`
    padding: 0 16px;
    width: 20%;
    min-width: 120px;
    overflow: hidden;

    /* Handle date range content overflow */
    > div {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

const CreatedCell = styled(TableCell)`
    padding: 0 16px;
    width: 15%;
    min-width: 100px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const ActionsCell = styled(TableCell)`
    padding: 0 16px;
    width: 80px;
    min-width: 80px;
    max-width: 80px;
    overflow: hidden;
    text-align: center;
`;

const StyledTableRow = styled(TableRow)<{ isEven: boolean }>`
    background-color: ${(props) => (props.isEven ? props.theme.colors.gray : props.theme.colors.white)};

    &:hover {
        background-color: ${(props) => props.theme.colors.primary.light} !important;
    }

    /* Ensure selected rows have distinct styling */
    &.selected {
        background-color: ${(props) => props.theme.colors.primary.light} !important;

        &:hover {
            background-color: ${(props) => `${props.theme.colors.primary.main}08`} !important;
        }
    }
`;

export interface ClassTableRowProps {
    record: ClassWithInstructors;
    index: number;
    isSelected?: boolean;
    onSelectionChange?: (classUuid: string, checked: boolean, shiftKey?: boolean) => void;
    hasAnySelected?: boolean;
    onArchive?: (classUuid: string, currentStatus: string) => void;
    columnVisibility: ColumnVisibility;
}

const ClassTableRow = ({
    record,
    index,
    isSelected = false,
    onSelectionChange,
    hasAnySelected = false,
    onArchive,
    columnVisibility
}: ClassTableRowProps) => {
    const { path } = useRouteMatch();
    const { setSelectedClass, setSelectedDownloadTranscriptsClass } = useContainer(ClassesContainer);

    const [anchorEl, setAnchorEl] = useState(null);

    const { t } = useTranslation('admin_settings');

    const isDraft = record.status === ClassStatus.Draft;
    const isArchived = record.status === ClassStatus.Archived;
    const canPerformActions = !isDraft && !isArchived;
    const isEven = index % 2 === 0;

    const classLink = useMemo(() => {
        const name = new URLSearchParams({ className: record.class_name });
        return `${path}/${record.class_uuid}?${name.toString()}`;
    }, [path, record.class_uuid, record.class_name]);

    const copyUrl = useMemo(() => {
        const { vanityUrl } = getUrls();
        return `${vanityUrl}/class/${record.class_uuid}`;
    }, [record.class_uuid]);

    const renderClassName = useCallback((classRecord) => {
        const isDraft = classRecord.status === ClassStatus.Draft;
        const isArchived = classRecord.status === ClassStatus.Archived;

        return (
            <div className="class-name-content">
                <span className="class-name-text">{classRecord.class_name}</span>
                <div className="class-status-chips">
                    {isDraft && <DraftChip />}
                    {isArchived && <ArchiveChip />}
                </div>
            </div>
        );
    }, []);

    const onCopyURL = useCallback(() => {
        navigator.clipboard.writeText(copyUrl);
    }, [copyUrl]);

    const onActionsClick = useCallback((event) => {
        setAnchorEl(event.currentTarget);
    }, []);

    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    const handleDownloadChatClick = useCallback(() => {
        setSelectedClass(record);
        handleClose();
    }, [record, handleClose, setSelectedClass]);

    const handleDownloadTranscriptsClick = useCallback(() => {
        setSelectedDownloadTranscriptsClass(record);
        handleClose();
    }, [record, handleClose, setSelectedDownloadTranscriptsClass]);

    const handleCopyUrlClick = useCallback(() => {
        onCopyURL();
        handleClose();
    }, [onCopyURL, handleClose]);

    const handleCheckboxChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            if (onSelectionChange) {
                const mouseEvent = event.nativeEvent as MouseEvent;
                onSelectionChange(record.class_uuid, event.target.checked, mouseEvent.shiftKey);
            }
        },
        [onSelectionChange, record.class_uuid]
    );

    const handleArchiveClick = useCallback(() => {
        if (onArchive) {
            onArchive(record.class_uuid, record.status);
        }
        handleClose();
    }, [onArchive, handleClose, record.class_uuid, record.status]);

    const getArchiveTooltipTitle = useCallback(() => {
        if (hasAnySelected) {
            return t('classes.use-bulk-actions');
        }
        if (isDraft) {
            return t('classes.draft-cannot-be-archived');
        }
        return '';
    }, [hasAnySelected, isDraft, t]);

    const getArchiveHelpText = useCallback(() => {
        if (hasAnySelected) {
            return t('classes.use-bulk-actions');
        }
        return t('classes.draft-cannot-be-archived');
    }, [hasAnySelected, t]);

    const archiveMenuItem = useMemo(() => {
        if (!onArchive) return null;

        const isDisabled = hasAnySelected || isDraft;
        const menuItemProps = {
            onClick: handleArchiveClick,
            disabled: isDisabled,
            role: 'menuitem' as const,
            'aria-label': isArchived
                ? t('classes.unarchive-class-for', { className: record.class_name })
                : t('classes.archive-class-for', { className: record.class_name }),
            'data-testid': isArchived ? 'unarchive-class-menu-item' : 'archive-class-menu-item',
            ...(isDisabled && { 'aria-describedby': `archive-help-${record.class_uuid}` })
        };

        const menuItemContent = (
            <>
                {isArchived ? t('classes.unarchive-class') : t('classes.archive-class')}
                {isDisabled && (
                    <span id={`archive-help-${record.class_uuid}`} className="sr-only">
                        {getArchiveHelpText()}
                    </span>
                )}
            </>
        );

        const menuItem = <MenuItem {...menuItemProps}>{menuItemContent}</MenuItem>;

        return isDisabled ? (
            <Tooltip title={getArchiveTooltipTitle()} arrow disableInteractive>
                <span>{menuItem}</span>
            </Tooltip>
        ) : (
            menuItem
        );
    }, [
        onArchive,
        hasAnySelected,
        isDraft,
        isArchived,
        handleArchiveClick,
        t,
        record.class_name,
        record.class_uuid,
        getArchiveHelpText,
        getArchiveTooltipTitle
    ]);

    return (
        <StyledTableRow
            key={record.class_uuid}
            className={isSelected ? 'selected' : ''}
            role="row"
            aria-selected={isSelected}
            isEven={isEven}
        >
            <CheckboxCell role="gridcell" data-testid="checkbox-cell">
                {onSelectionChange && (
                    <Tooltip title={isDraft ? t('classes.draft-cannot-be-archived') : ''} arrow>
                        <span>
                            <Checkbox
                                checked={isSelected}
                                onChange={handleCheckboxChange}
                                disabled={isDraft}
                                size="small"
                                aria-label={t('classes.select-class-for', { className: record.class_name })}
                                aria-describedby={isDraft ? `draft-help-${record.class_uuid}` : undefined}
                                inputProps={{
                                    'aria-rowindex': index + 1
                                }}
                            />
                            {isDraft && (
                                <span id={`draft-help-${record.class_uuid}`} className="sr-only">
                                    {t('classes.draft-cannot-be-archived')}
                                </span>
                            )}
                        </span>
                    </Tooltip>
                )}
            </CheckboxCell>
            {columnVisibility.className && (
                <ClassNameCell role="gridcell" data-testid="class-name-cell">
                    <ClassLink
                        to={classLink}
                        aria-label={t('classes.edit-class-for', { className: record.class_name })}
                    >
                        {renderClassName(record)}
                    </ClassLink>
                </ClassNameCell>
            )}
            {columnVisibility.instructor && (
                <InstructorCell role="gridcell" data-testid="instructor-cell">
                    <Instructors instructors={record.instructors} />
                </InstructorCell>
            )}
            {columnVisibility.dates && (
                <DateCell role="gridcell" data-testid="dates-cell">
                    <ClassesDateRange start={record.starting_at} end={record.ending_at} />
                </DateCell>
            )}
            {columnVisibility.created && (
                <CreatedCell role="gridcell" data-testid="created-cell">
                    {record.created_at && new Date(record.created_at).toLocaleDateString()}
                </CreatedCell>
            )}
            <ActionsCell role="gridcell" data-testid="actions-cell">
                <StyledIconButton
                    size="small"
                    onClick={onActionsClick}
                    aria-label={t('classes.actions-for-class', { className: record.class_name })}
                    aria-expanded={Boolean(anchorEl)}
                    aria-haspopup="menu"
                    id={`actions-button-${record.class_uuid}`}
                    data-testid="actions-button"
                >
                    <MoreHorizIcon />
                </StyledIconButton>
            </ActionsCell>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                role="menu"
                aria-label={t('classes.actions-for-class', { className: record.class_name })}
                aria-labelledby={`actions-button-${record.class_uuid}`}
                data-testid="actions-menu"
                MenuListProps={{
                    'aria-orientation': 'vertical',
                    role: 'menu'
                }}
            >
                <MenuItem
                    onClick={handleClose}
                    role="menuitem"
                    aria-label={t('classes.edit-class-for', { className: record.class_name })}
                    data-testid="edit-class-menu-item"
                >
                    <MenuLink to={classLink}>{t('classes.edit-class')}</MenuLink>
                </MenuItem>
                {archiveMenuItem}

                {canPerformActions && (
                    <MenuItem
                        onClick={handleDownloadChatClick}
                        role="menuitem"
                        aria-label={t('classes.download-chat-for-class', { className: record.class_name })}
                        data-testid="download-chat-menu-item"
                    >
                        {t('classes.download-chat')}
                    </MenuItem>
                )}
                {canPerformActions && (
                    <MenuItem
                        onClick={handleDownloadTranscriptsClick}
                        role="menuitem"
                        aria-label={t('classes.download-transcript-for-class', { className: record.class_name })}
                        data-testid="download-transcript-menu-item"
                    >
                        {t('classes.download-transcript')}
                    </MenuItem>
                )}
                {canPerformActions && (
                    <MenuItem
                        onClick={handleCopyUrlClick}
                        role="menuitem"
                        aria-label={t('classes.copy-url-for-class', { className: record.class_name })}
                        data-testid="copy-url-menu-item"
                    >
                        {t('classes.copy-url')}
                    </MenuItem>
                )}
            </Menu>
        </StyledTableRow>
    );
};

// Custom comparison function for React.memo to optimize re-renders
const areEqual = (prevProps: ClassTableRowProps, nextProps: ClassTableRowProps) => {
    const { record: prevRecord, columnVisibility: prevColumns } = prevProps;
    const { record: nextRecord, columnVisibility: nextColumns } = nextProps;

    // Early exit on reference equality (most common case for stable objects)
    if (prevRecord === nextRecord && prevColumns === nextColumns) {
        // Still need to check primitive props and callbacks that might change independently
        return (
            prevProps.index === nextProps.index &&
            prevProps.isSelected === nextProps.isSelected &&
            prevProps.hasAnySelected === nextProps.hasAnySelected &&
            prevProps.onSelectionChange === nextProps.onSelectionChange &&
            prevProps.onArchive === nextProps.onArchive
        );
    }

    // Check primitive props and callbacks first (fastest comparisons)
    if (
        prevProps.index !== nextProps.index ||
        prevProps.isSelected !== nextProps.isSelected ||
        prevProps.hasAnySelected !== nextProps.hasAnySelected ||
        prevProps.onSelectionChange !== nextProps.onSelectionChange ||
        prevProps.onArchive !== nextProps.onArchive
    ) {
        return false;
    }

    // Check record properties (exit early on first difference)
    if (
        prevRecord.class_uuid !== nextRecord.class_uuid ||
        prevRecord.class_name !== nextRecord.class_name ||
        prevRecord.status !== nextRecord.status ||
        prevRecord.created_at !== nextRecord.created_at ||
        prevRecord.starting_at !== nextRecord.starting_at ||
        prevRecord.ending_at !== nextRecord.ending_at
    ) {
        return false;
    }

    // Check column visibility (likely to be stable, so check after record)
    if (
        prevColumns.className !== nextColumns.className ||
        prevColumns.instructor !== nextColumns.instructor ||
        prevColumns.dates !== nextColumns.dates ||
        prevColumns.created !== nextColumns.created
    ) {
        return false;
    }

    // Check instructors array (most expensive, so check last)
    const prevInstructors = prevRecord.instructors;
    const nextInstructors = nextRecord.instructors;

    // Quick reference check before deep comparison
    if (prevInstructors === nextInstructors) {
        return true;
    }

    if (prevInstructors.length !== nextInstructors.length) {
        return false;
    }

    // Use every() for cleaner code and potential early exit
    return prevInstructors.every((prevInstructor, i) => {
        const nextInstructor = nextInstructors[i];
        return prevInstructor.user_name === nextInstructor.user_name && prevInstructor.email === nextInstructor.email;
    });
};

export default memo(ClassTableRow, areEqual);
